import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from "../../../context/UserContext";
import { getImageCacheKey, getImageUrl, invalidateImageCache } from '../../../lib/helpers';
import { isValidEmail } from '../../../lib/email';
import { isValidSocioPhone } from '../../../lib/phone';
import { nuevaTiendaPrincipal } from '../../../lib/staffPrimaryVenue';
import CustomModal from '../../Elements/CustomModal';
import {
  DEFAULT_MEMBERSHIP_DURATIONS,
  findMatchingDurationPresetId,
  formatDateForApi,
  getConsumptionTypeValue,
  getProfileDateRaw,
  MembershipDurationEditor,
  parseDayFromApi,
} from '../../Elements/MembershipDateFields';

function EmployeeModal({ active, handleModal, token, id, staffData, setErrorMessage, onSuccess }) {
  const [tokenFromContext, , , venueID] = useContext(UserContext);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Usar el token del prop si está disponible, sino del contexto
  const authToken = token || tokenFromContext;
  const isEditMode = !!id;
  
  // Fecha de hoy en formato YYYY-MM-DD para usar en validaciones
  const today = new Date().toISOString().split('T')[0];

  // Estado centralizado del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: '', // Opcional - si no se proporciona, el backend genera una temporal
    confirmationPassword: '',
    first_name: '',
    last_name: '',
    national_id: '',
    birth_date: '',
    postal_code: '',
    city: '',
    address: '',
    phone: '',
    credit_balance: '',
    discount: 'Sin descuento',
    fee: '',
    max_monthly_consumption: '',
    consumption_type: '',
    guarantor_member: '',
    can_manage_products: false,
    can_recharge_dispensario: false,
  });

  const [inscripcionInput, setInscripcionInput] = useState('');
  const [datePickerInscripcion, setDatePickerInscripcion] = useState('');
  const [caducidadInput, setCaducidadInput] = useState('');
  const [datePickerCaducidad, setDatePickerCaducidad] = useState('');
  const [selectedDurationId, setSelectedDurationId] = useState(null);
  const [durations, setDurations] = useState(DEFAULT_MEMBERSHIP_DURATIONS);

  const [selectedShop, setSelectedShop] = useState([]);
  // Si el owner desmarca la tienda principal del staff, a cuál pasa (null = sigue igual).
  const nuevaPrincipalId = isEditMode ? nuevaTiendaPrincipal(staffData, selectedShop) : null;
  const [shop, setShop] = useState([]);
  const [image, setImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const blobUrlRef = useRef(null);
  const lastEmployeeModalModeRef = useRef(null);
  const lastLoadedEditIdRef = useRef(null);

  // Función helper para formatear fechas a YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    // Si ya está en formato YYYY-MM-DD, devolverlo
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Intentar parsear la fecha
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null; // Fecha inválida
    }
    
    // Formatear a YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchShops = async () => {
      if (!authToken) return;
      
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authToken,
        },
      };
      try {
        const response = await fetch(`/v1/venues/all`, requestOptions);
        if (!response.ok) throw new Error("Could not fetch venues");
        const data = await response.json();
        const venues = Array.isArray(data) ? data : [];
        setShop(venues);
        
        // Si solo hay una tienda y no estamos en modo edición, seleccionarla automáticamente
        if (venues.length === 1 && !id && selectedShop.length === 0) {
          setSelectedShop([venues[0].id]);
        }
      } catch (error) {
        console.error("Error fetching venues:", error);
        if (setErrorMessage) {
          setErrorMessage('Error al obtener tiendas');
        }
      }
    };
    
    fetchShops();
  }, [authToken, setErrorMessage, id, selectedShop.length]);

  // Cerrar modal: limpiar todo (no agrupar con `active && !id` para no dejar estado colgado si falta staffData un instante)
  useEffect(() => {
    if (active) return;
    lastEmployeeModalModeRef.current = 'closed';
    lastLoadedEditIdRef.current = null;
    setFormData({
      email: '',
      password: '',
      confirmationPassword: '',
      first_name: '',
      last_name: '',
      national_id: '',
      birth_date: '',
      postal_code: '',
      city: '',
      address: '',
      phone: '',
      credit_balance: '',
      discount: 'Sin descuento',
      fee: '',
      max_monthly_consumption: '',
      consumption_type: '',
      guarantor_member: '',
    });
    setSelectedShop([]);
    setLocalError('');
    setImage(null);
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    setCurrentImageUrl(null);
    setInscripcionInput('');
    setDatePickerInscripcion('');
    setCaducidadInput('');
    setDatePickerCaducidad('');
    setSelectedDurationId(null);
    setDurations(DEFAULT_MEMBERSHIP_DURATIONS);
  }, [active]);

  // Modo crear: solo reset al entrar en crear (no en cada re-render mientras se escribe)
  useEffect(() => {
    if (!active || id) return;
    if (lastEmployeeModalModeRef.current === 'create') return;
    lastEmployeeModalModeRef.current = 'create';
    setFormData({
      email: '',
      password: '',
      confirmationPassword: '',
      first_name: '',
      last_name: '',
      national_id: '',
      birth_date: '',
      postal_code: '',
      city: '',
      address: '',
      phone: '',
      credit_balance: '',
      discount: 'Sin descuento',
      fee: '',
      max_monthly_consumption: '',
      consumption_type: '',
      guarantor_member: '',
    });
    setSelectedShop([]);
    setLocalError('');
    setImage(null);
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    setCurrentImageUrl(null);
    setInscripcionInput('');
    setDatePickerInscripcion('');
    setCaducidadInput('');
    setDatePickerCaducidad('');
    setSelectedDurationId(null);
    setDurations(DEFAULT_MEMBERSHIP_DURATIONS);
  }, [active, id]);

  // Editar: hidratar siempre que haya id y datos del servidor
  useEffect(() => {
    if (!active || !id || !staffData) return;
    if (lastLoadedEditIdRef.current === id) return;
    lastLoadedEditIdRef.current = id;
    lastEmployeeModalModeRef.current = 'edit';
    console.log('📝 Loading staff data for editing:', staffData);
    const rawFee = String(staffData.profile?.fee ?? '').trim();
    let feeField = '';
    if (rawFee) {
      const compact = rawFee.replace(/\s/g, '');
      const legacy = /^0[,.]00€?$/.test(compact) || rawFee === '0.00 €' || rawFee === '0,00 €';
      feeField = legacy ? '' : rawFee;
    }
    setFormData({
      email: staffData.email || '',
      password: '',
      confirmationPassword: '',
      first_name: staffData.profile?.first_name || '',
      last_name: staffData.profile?.last_name || '',
      national_id: staffData.profile?.national_id?.toString() || '',
      birth_date: staffData.profile?.birth_date ? formatDate(staffData.profile.birth_date) : '',
      postal_code: staffData.profile?.postal_code?.toString() || '',
      city: staffData.profile?.city || '',
      address: staffData.profile?.address || '',
      phone: staffData.profile?.phone?.toString() || '',
      credit_balance: staffData.profile?.credit_balance?.toString() || '',
      discount: staffData.profile?.discount || 'Sin descuento',
      fee: feeField,
      max_monthly_consumption: staffData.profile?.max_monthly_consumption != null
        ? String(staffData.profile.max_monthly_consumption)
        : '',
      consumption_type: getConsumptionTypeValue(staffData),
      guarantor_member: staffData.profile?.guarantor_member || '',
      can_manage_products: !!staffData.can_manage_products,
      can_recharge_dispensario: !!staffData.can_recharge_dispensario,
    });

    const enr = parseDayFromApi(getProfileDateRaw(staffData, 'enrollment_date'));
    const exp = parseDayFromApi(getProfileDateRaw(staffData, 'expiration_date'));
    setInscripcionInput(enr.display);
    setDatePickerInscripcion(enr.iso);
    setCaducidadInput(exp.display);
    setDatePickerCaducidad(exp.iso);

    if (staffData.profile?.documents_path) {
      setCurrentImageUrl(getImageUrl(staffData.profile.documents_path, {
        cacheKey: getImageCacheKey('staff', staffData.id || id),
      }));
    } else {
      setCurrentImageUrl(null);
    }

    // Multi-tienda: pre-seleccionar todas las tiendas asignadas (fallback a la principal).
    if (Array.isArray(staffData.assigned_venues) && staffData.assigned_venues.length > 0) {
      setSelectedShop(staffData.assigned_venues.map((v) => (typeof v === 'string' ? v : (v?.id ?? v))));
    } else if (staffData.venue_id) {
      setSelectedShop([staffData.venue_id]);
    } else {
      setSelectedShop([]);
    }

    setImage(null);
    setLocalError('');
  }, [active, id, staffData]);

  useEffect(() => {
    if (!active || !id || !authToken) return;
    const shopId = staffData?.venue_id || venueID;
    if (!shopId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${shopId}/membership-durations`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) setDurations(data);
      } catch {
        /* mantener DEFAULT_MEMBERSHIP_DURATIONS */
      }
    })();
    return () => { cancelled = true; };
  }, [active, id, authToken, staffData?.venue_id, venueID]);

  useEffect(() => {
    if (!active || !id || !staffData || !durations.length) return;
    const toDayIso = (raw) => {
      if (raw == null || raw === '') return '';
      const s = String(raw).split('T')[0];
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
    };
    const enroll = toDayIso(getProfileDateRaw(staffData, 'enrollment_date'));
    const expire = toDayIso(getProfileDateRaw(staffData, 'expiration_date'));
    setSelectedDurationId(findMatchingDurationPresetId(enroll, expire, durations));
  }, [active, id, staffData, durations]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      setCurrentImageUrl(url);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Multi-tienda: marca/desmarca una tienda en el selector de checkboxes.
  const handleSelectShop = (e) => {
    const shopId = e.target.value;
    if (selectedShop.includes(shopId)) {
      setSelectedShop((prev) => prev.filter((sid) => sid !== shopId));
    } else {
      setSelectedShop((prev) => [...prev, shopId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsSubmitting(true);
    
    // Validaciones
    if (!isValidEmail(formData.email)) {
      const errorMsg = 'Por favor, ingresa un email válido';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }

    // El backend valida E.164 en VenueEmployeeCreate. Sin esta comprobación previa, un
    // teléfono sin prefijo salía como error de validación del servidor (issue en Sentry)
    // en vez de avisar aquí. Vacío se acepta: el teléfono es opcional.
    if (!isValidSocioPhone(formData.phone || '')) {
      const errorMsg = 'Teléfono no válido. Déjalo vacío o usa formato internacional (ej. +34 600 123 456).';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }

    // Validar contraseña solo si se proporciona (es opcional)
    if (formData.password || formData.confirmationPassword) {
      if (formData.password !== formData.confirmationPassword) {
        const errorMsg = 'Las contraseñas no coinciden';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
      
      if (formData.password.length > 0 && formData.password.length < 6) {
        const errorMsg = 'La contraseña debe tener al menos 6 caracteres';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
    }
    
    // Validar campos requeridos
    if (!formData.first_name || !formData.last_name || !formData.national_id) {
      const errorMsg = 'Por favor, completa todos los campos requeridos (Nombre, Apellidos, DNI)';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }
    
    // Multi-tienda: el trabajador puede asignarse a varias tiendas (checkboxes).
    const assignedVenues = (selectedShop && selectedShop.length > 0)
      ? selectedShop.map((sid) => String(sid))
      : (venueID ? [String(venueID)] : (shop && shop.length > 0 ? [String(shop[0].id)] : []));

    if (assignedVenues.length === 0) {
      const errorMsg = 'Selecciona al menos una tienda para el trabajador.';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }
    // Tienda principal: en edición conserva la existente si sigue marcada; si no, la 1ª.
    const venueIdToUse = (isEditMode && staffData?.venue_id && assignedVenues.includes(String(staffData.venue_id)))
      ? String(staffData.venue_id)
      : assignedVenues[0];

    if (isEditMode) {
      const enrIso = formatDateForApi(inscripcionInput);
      const expIso = formatDateForApi(caducidadInput);
      if (enrIso !== '2000-01-01' && expIso !== '2000-01-01' && expIso <= enrIso) {
        const errorMsg = 'La fecha de caducidad debe ser posterior a la inscripción.';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
    }
    
    // Formatear fechas correctamente - usar datos existentes en modo edición
    const birthDate = formatDate(formData.birth_date) || (isEditMode && staffData?.profile?.birth_date ? formatDate(staffData.profile.birth_date) : "2000-01-01");
    const enrollFallback = (isEditMode && getProfileDateRaw(staffData, 'enrollment_date') && formatDate(getProfileDateRaw(staffData, 'enrollment_date'))) || today;
    const expireFallback = (isEditMode && getProfileDateRaw(staffData, 'expiration_date') && formatDate(getProfileDateRaw(staffData, 'expiration_date'))) || '2099-12-31';
    const parsedEnr = formatDateForApi(inscripcionInput);
    const parsedExp = formatDateForApi(caducidadInput);
    const enrollmentDate = isEditMode
      ? (parsedEnr !== '2000-01-01' ? parsedEnr : enrollFallback)
      : today;
    const expirationDate = isEditMode
      ? (parsedExp !== '2000-01-01' ? parsedExp : expireFallback)
      : '2099-12-31';
    
    // Asegurarse de que venue_id sea un string (UUID)
    const venueIdString = typeof venueIdToUse === 'string' ? venueIdToUse : String(venueIdToUse);
    const guarantorMemberTrimmed = (formData.guarantor_member || '').trim();

    // Construir el payload - usar datos del formulario o valores existentes en modo edición
    const staffPayload = {
      email: formData.email.trim(),
      password: formData.password || undefined, // Opcional - si está vacío, el backend no cambia la contraseña en edición
      venue_id: venueIdString,
      assigned_venues: assignedVenues, // multi-tienda: todas las tiendas marcadas
      can_manage_products: !!formData.can_manage_products, // permiso de gestión de productos
      can_recharge_dispensario: !!formData.can_recharge_dispensario, // estrella: recargar dispensario
      profile: {
        first_name: formData.first_name || (isEditMode ? staffData?.profile?.first_name : "Staff"),
        last_name: formData.last_name || (isEditMode ? staffData?.profile?.last_name : "Nuevo"),
        national_id: (formData.national_id || '').trim().toUpperCase()
          || (isEditMode
            ? String(staffData?.profile?.national_id || '')
            : String(Math.floor(10000000 + Math.random() * 89999999))),
        birth_date: birthDate,
        postal_code: parseInt(formData.postal_code) || (isEditMode ? staffData?.profile?.postal_code : 28001),
        city: formData.city || (isEditMode ? staffData?.profile?.city : "Madrid"),
        address: formData.address ?? (isEditMode ? staffData?.profile?.address : "") ?? "",
        phone: formData.phone ? formData.phone.toString() : (isEditMode ? (staffData?.profile?.phone || "") : ""),
        credit_balance: parseFloat(formData.credit_balance) || (isEditMode ? (staffData?.profile?.credit_balance || 0.0) : 0.0),
        enrollment_date: enrollmentDate,
        expiration_date: expirationDate,
        guarantor_member: guarantorMemberTrimmed || null,
        cost_line: isEditMode ? (staffData?.profile?.cost_line || "Defecto") : "Defecto",
        ...(isEditMode
          ? {
              discount: formData.discount || 'Sin descuento',
              fee: formData.fee.trim() || '0.00 €',
              max_monthly_consumption: parseInt(formData.max_monthly_consumption, 10) || 0,
              consumption_type: {
                ludenos: formData.consumption_type === 'ludenos',
                terap: formData.consumption_type === 'terap',
              },
            }
          : {
              discount: 'Sin descuento',
              fee: '0.00 €',
              max_monthly_consumption: 0,
              consumption_type: { ludenos: false, terap: false },
            }),
      }
    };
    
    const formDataToSend = new FormData();
    formDataToSend.append('staff_data', JSON.stringify(staffPayload));
    
    // Agregar imagen si se seleccionó (el backend espera 'documents')
    if (image) {
      formDataToSend.append('documents', image);
    }
    
    try {
      const endpoint = isEditMode ? `/v1/staff/${id}` : '/v1/staff';
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(endpoint, {
        method: method,
        headers: { 
          Authorization: "Bearer " + authToken 
        },
        body: formDataToSend,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `Error al ${isEditMode ? 'actualizar' : 'crear'} trabajador` }));
        
        // Manejar errores de validación de Pydantic que vienen como array
        let errorMsg = `Error al ${isEditMode ? 'actualizar' : 'crear'} trabajador`;
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Es un array de errores de validación
            errorMsg = errorData.detail.map(err => {
              const field = err.loc ? err.loc.join('.') : 'campo';
              // Mejorar mensajes de error comunes
              let msg = err.msg;
              if (msg.includes('national_id') || msg.includes('DNI') || msg.toLowerCase().includes('dni')) {
                msg = 'El DNI ya existe. Por favor, verifica que no esté duplicado.';
              } else if (msg.includes('email') || msg.toLowerCase().includes('email')) {
                msg = 'El email ya existe. Por favor, usa otro email.';
              }
              return field ? `${field}: ${msg}` : msg;
            }).join('; ');
          } else if (typeof errorData.detail === 'string') {
            // Mejorar mensajes de error comunes en strings
            let detailMsg = errorData.detail;
            if (detailMsg.includes('national_id') || detailMsg.includes('DNI') || detailMsg.toLowerCase().includes('dni') || detailMsg.toLowerCase().includes('ya existe')) {
              detailMsg = 'El DNI ya existe. Por favor, verifica que no esté duplicado.';
            } else if (detailMsg.includes('email') || detailMsg.toLowerCase().includes('email')) {
              detailMsg = 'El email ya existe. Por favor, usa otro email.';
            }
            errorMsg = detailMsg;
          } else {
            errorMsg = JSON.stringify(errorData.detail);
          }
        }
        
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
      
      const data = await response.json();
      invalidateImageCache(getImageCacheKey('staff', data.id || id));
      
      // Limpiar formulario
      setFormData({
        email: '',
        password: '',
        confirmationPassword: '',
        first_name: '',
        last_name: '',
        national_id: '',
        birth_date: '',
        postal_code: '',
        city: '',
        address: '',
        phone: '',
        credit_balance: '',
        discount: 'Sin descuento',
        fee: '',
        max_monthly_consumption: '',
        consumption_type: '',
        guarantor_member: '',
      });
      setSelectedShop([]);
      setImage(null);
      setCurrentImageUrl(null);
      setLocalError('');
      setIsSubmitting(false);
      setInscripcionInput('');
      setDatePickerInscripcion('');
      setCaducidadInput('');
      setDatePickerCaducidad('');
      setSelectedDurationId(null);
      setDurations(DEFAULT_MEMBERSHIP_DURATIONS);
      
      // Notificar al componente padre que se creó exitosamente
      if (onSuccess) {
        onSuccess(data);
      }
      
      // Cerrar modal - el componente padre debería recargar
      handleModal();
    } catch (error) {
      console.error("Error creating staff:", error);
      const errorMsg = error.message || 'Ocurrió un error al crear el trabajador';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal
      active={active}
      handleModal={handleModal}
      title={id ? 'Editar Trabajador' : 'Crear Trabajador'}
      size="wide"
      titleId="employee-modal-title"
      footer={
        <>
          <button type="button" className="wa-btn-secondary" onClick={handleModal} disabled={isSubmitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="employee-modal-form"
            disabled={isSubmitting}
            className="wa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Guardando...' : (id ? 'Actualizar' : 'Registrar')}
          </button>
        </>
      }
    >
      <form id="employee-modal-form" onSubmit={handleSubmit} className="wa-form space-y-4" autoComplete="off">
        {localError && (
          <div className="wa-form-error relative pr-10" role="alert">
            <strong>Error: </strong>
            <span>{localError}</span>
            <button
              type="button"
              className="absolute top-2 right-2 text-red-800 hover:opacity-80"
              onClick={() => setLocalError('')}
              aria-label="Cerrar mensaje"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Columna izquierda */}
                <div className="space-y-6">
                  {/* Nombre, Apellidos */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Nombre *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="nombre"
                        className="wa-input"
                        required
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Apellidos *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="apellidos"
                        className="wa-input"
                        required
                      />
                    </div>
                  </div>

                  {/* DNI, Fecha Nacimiento, CP, Ciudad */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">DNI / NIE *</label>
                      <input
                        type="text"
                        name="national_id"
                        value={formData.national_id}
                        onChange={(e) => setFormData((prev) => ({ ...prev, national_id: e.target.value }))}
                        placeholder="12345678A"
                        className="wa-input"
                        inputMode="text"
                        autoCapitalize="characters"
                        autoComplete="off"
                        spellCheck={false}
                        required
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Fecha Nacimiento</label>
                      <input
                        type="date"
                        name="birth_date"
                        value={formData.birth_date}
                        onChange={handleChange}
                        className="wa-input"
                        max={today}
                      />
                    </div>
                    <div className="flex-1 min-w-[100px]">
                      <label className="block text-sm font-medium mb-1">CP</label>
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => {
                          // Permitir solo números para código postal
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData(prev => ({
                            ...prev,
                            postal_code: value
                          }));
                        }}
                        placeholder="28001"
                        maxLength={5}
                        className="wa-input"
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Ciudad</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="ciudad"
                        className="wa-input"
                      />
                    </div>
                  </div>

                  {/* Dirección */}
                  <div className="flex flex-wrap gap-4">
                    <div className="w-full">
                      <label className="block text-sm font-medium mb-1">Dirección</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="dirección"
                        className="wa-input"
                      />
                    </div>
                  </div>

                  <div className="wa-form-field w-full">
                    <label className="wa-label">Socio avalado</label>
                    <input
                      type="text"
                      name="guarantor_member"
                      value={formData.guarantor_member}
                      onChange={handleChange}
                      placeholder="socio avalado"
                      className="wa-input"
                    />
                  </div>

                  {id && (
                  <>
                  {/* Descuento, cuota, consumo — mismo criterio que editar socio */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                    <div className="wa-form-field min-w-0">
                      <label className="wa-label">Descuento</label>
                      <select name="discount" value={formData.discount} onChange={handleChange} className="wa-select w-full">
                        <option>Sin descuento</option>
                        <option>5%</option>
                        <option>10%</option>
                        <option>15%</option>
                        <option>20%</option>
                        <option>50%</option>
                        <option>100%</option>
                      </select>
                    </div>
                    <div className="wa-form-field min-w-0">
                      <label className="wa-label">Cuota</label>
                      <input
                        type="text"
                        name="fee"
                        value={formData.fee}
                        onChange={handleChange}
                        placeholder="ej: Mensual"
                        className="wa-input"
                      />
                    </div>
                    <div className="wa-form-field min-w-0">
                      <label className="wa-label">Consumo máx/mes</label>
                      <input
                        type="number"
                        name="max_monthly_consumption"
                        value={formData.max_monthly_consumption}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        className="wa-input"
                      />
                    </div>
                    <div className="wa-form-field min-w-0">
                      <span className="wa-label">Tipo de consumo</span>
                      <div className="flex flex-wrap gap-4 items-center min-h-[40px]">
                        <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                          <input
                            type="radio"
                            name="consumption_type_staff"
                            value="ludenos"
                            checked={formData.consumption_type === 'ludenos'}
                            onChange={() => setFormData((prev) => ({ ...prev, consumption_type: 'ludenos' }))}
                          />
                          Lúd.
                        </label>
                        <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                          <input
                            type="radio"
                            name="consumption_type_staff"
                            value="terap"
                            checked={formData.consumption_type === 'terap'}
                            onChange={() => setFormData((prev) => ({ ...prev, consumption_type: 'terap' }))}
                          />
                          Terap.
                        </label>
                      </div>
                    </div>
                  </div>

                  <MembershipDurationEditor
                    durations={durations}
                    selectedDurationId={selectedDurationId}
                    setSelectedDurationId={setSelectedDurationId}
                    inscripcionInput={inscripcionInput}
                    setInscripcionInput={setInscripcionInput}
                    datePickerInscripcion={datePickerInscripcion}
                    setDatePickerInscripcion={setDatePickerInscripcion}
                    caducidadInput={caducidadInput}
                    setCaducidadInput={setCaducidadInput}
                    datePickerCaducidad={datePickerCaducidad}
                    setDatePickerCaducidad={setDatePickerCaducidad}
                  />
                  </>
                  )}

                  {/* Teléfono, Email */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Teléfono</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          // Permitir dígitos y el prefijo internacional "+" (E.164). Opcional.
                          const value = e.target.value.replace(/[^\d+]/g, '');
                          setFormData(prev => ({
                            ...prev,
                            phone: value
                          }));
                        }}
                        // El validador del backend exige E.164 (con prefijo). El placeholder
                        // anterior, "600000000", enseñaba justo el formato que se rechaza:
                        // quien lo copiaba se comía un error de validación (issue en Sentry).
                        placeholder="+34600000000"
                        className="wa-input"
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="email@ejemplo.com"
                        className="wa-input"
                        autoComplete="off"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Columna derecha */}
                <div className="space-y-6">
                  {/* Contraseñas - Opcionales */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Contraseña (Opcional)
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="wa-input"
                      placeholder="Dejar vacío para generar automáticamente"
                      autoComplete="new-password"
                      minLength={6}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Si no se proporciona, se generará una contraseña temporal automáticamente
                    </p>
                  </div>

                  {formData.password && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Confirmar Contraseña</label>
                      <input
                        type="password"
                        name="confirmationPassword"
                        value={formData.confirmationPassword}
                        onChange={handleChange}
                        className="wa-input"
                        autoComplete="new-password"
                        minLength={6}
                      />
                    </div>
                  )}

                  {/* Saldo inicial */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Saldo Inicial</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="credit_balance"
                      value={formData.credit_balance}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="wa-input"
                    />
                  </div>

                  {/* Tiendas asignadas (multi-tienda, mismo patrón que Crear Propietario) */}
                  <div>
                    <label htmlFor="shop_employee" className="block text-sm font-medium mb-1">
                      Tiendas Asignadas *
                    </label>
                    <div id="shop_employee" className="mt-2 max-h-[200px] overflow-y-auto space-y-2 rounded-md border border-gray-200 p-2">
                      {shop.length === 0 && (
                        <p className="text-xs text-gray-500">No hay tiendas disponibles.</p>
                      )}
                      {shop.map((shopItem) => (
                        <label key={shopItem.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            value={shopItem.id}
                            checked={selectedShop.includes(shopItem.id)}
                            onChange={handleSelectShop}
                            className="h-5 w-5"
                          />
                          <span>{shopItem.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Marca una o varias tiendas. El trabajador podrá cambiar entre ellas desde "Mis Tiendas".
                    </p>
                    {isEditMode && nuevaPrincipalId && (
                      <p
                        role="status"
                        data-testid="aviso-tienda-principal"
                        className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
                      >
                        Has desmarcado la tienda principal del trabajador. Pasará a ser{' '}
                        <strong>{shop.find((t) => String(t.id) === nuevaPrincipalId)?.name || 'otra de las marcadas'}</strong>
                        {' '}y recibirá allí un número de socio (puede cambiar).
                      </p>
                    )}
                  </div>

                  {/* Campo para subir imagen */}
                  <div>
                    <label htmlFor="image" className="block text-sm font-medium mb-1">
                      Imagen de Perfil <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {currentImageUrl ? (
                          <img
                            src={currentImageUrl}
                            alt="Vista previa"
                            className="h-28 w-28 rounded-full object-cover border border-gray-300"
                          />
                        ) : (
                          <div className="h-28 w-28 rounded-full bg-gray-200 flex items-center justify-center border border-gray-300">
                            <svg className="h-8 w-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="wa-file-input block w-full"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Selecciona una imagen JPG, PNG o GIF (máximo 5MB). Puedes dejarlo vacío.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Privilegios — último campo */}
                  <div className="mt-2">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Privilegios</h3>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, can_manage_products: !prev.can_manage_products }))}
                      aria-pressed={!!formData.can_manage_products}
                      className="flex items-start gap-3 w-full text-left p-3 rounded-lg border border-gray-200 hover:border-lime-400 transition-colors"
                    >
                      <span className={`text-2xl leading-none ${formData.can_manage_products ? 'text-yellow-400' : 'text-gray-300'}`}>
                        {formData.can_manage_products ? '★' : '☆'}
                      </span>
                      <span>
                        <span className="block font-medium text-gray-900">Puede gestionar productos</span>
                        <span className="block text-sm text-gray-500">Permite a este empleado editar/borrar productos y categorías.</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, can_recharge_dispensario: !prev.can_recharge_dispensario }))}
                      aria-pressed={!!formData.can_recharge_dispensario}
                      className="mt-2 flex items-start gap-3 w-full text-left p-3 rounded-lg border border-gray-200 hover:border-lime-400 transition-colors"
                    >
                      <span className={`text-2xl leading-none ${formData.can_recharge_dispensario ? 'text-yellow-400' : 'text-gray-300'}`}>
                        {formData.can_recharge_dispensario ? '★' : '☆'}
                      </span>
                      <span>
                        <span className="block font-medium text-gray-900">Puede recargar el dispensario</span>
                        <span className="block text-sm text-gray-500">Encargado del dispensario: puede meter/ajustar stock en los armarios de venta (traspaso, entrada, ajuste). El resto solo puede vender.</span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
      </form>
    </CustomModal>
  );
}

export default EmployeeModal;
